import React from 'react';

import {
  FaCamera,
  FaCheckCircle,
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaFilter,
  FaGift,
  FaHeart,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaPen,
  FaPlus,
  FaPlusCircle,
  FaRegBell,
  FaRegClock,
  FaRegQuestionCircle,
  FaRegStar,
  FaRegThumbsDown,
  FaRegThumbsUp,
  FaSearch,
  FaStar,
  FaTag,
  FaTimes,
  FaTimesCircle,
  FaTrash,
} from 'react-icons/fa';
import { IoMdEye, IoMdEyeOff } from 'react-icons/io';

import {
  AddCircle,
  AddToCart,
  Alert,
  AlertCircle,
  AlertTriangle,
  Calendar,
  Camera,
  CaretLeft,
  CaretRight,
  CaretUp,
  CheckCircle,
  Clock,
  Close,
  CloseCircle,
  DeliveryStatus,
  Dislike,
  Edit,
  EmptyStar,
  Favorites,
  FilledStar,
  Filter,
  Gift,
  HappyReturnsClock,
  HappyReturnsLeaf,
  HappyReturnsLocation,
  HappyReturnsOffer,
  Heart,
  Home,
  Information,
  Leaf,
  Like,
  LocationPin,
  Mail,
  NavigationLeft,
  Plus,
  PrinterReturnItems,
  QuestionCircle,
  Receipt,
  ScanBarcode,
  ScrubsThread,
  Shipping1,
  Shipping2,
  Stars,
  Tag1,
  Tag2,
  ToggleDown,
  ViewOff,
  ViewOn,
} from '../../fabletics';
import { type SavageIconTypes } from '../../savage/SavageIcons';
import CalendarIcon from '../CalendarIcon/CalendarIcon';
import CartIcon from '../CartIcon';
import EmailIcon from '../EmailIcon';
import FavoriteIcon from '../FavoriteIcon/FavoriteIcon';

export type IconName =
  | 'addCircle'
  | 'addToCart'
  | 'alert'
  | 'alertCircle'
  | 'alertTriangle'
  | 'calendar'
  | 'camera'
  | 'chevronLeft'
  | 'chevronRight'
  | 'chevronDown'
  | 'chevronUp'
  | 'cart'
  | 'checkCircle'
  | 'clock'
  | 'close'
  | 'closeCircle'
  | 'deliveryStatus'
  | 'dislike'
  | 'edit'
  | 'email'
  | 'emptyStar'
  | 'favorites'
  | 'filledStar'
  | 'filter'
  | 'gift'
  | 'happyReturnsClock'
  | 'happyReturnsLeaf'
  | 'happyReturnsLocation'
  | 'happyReturnsOffer'
  | 'heart'
  | 'home'
  | 'information'
  | 'leaf'
  | 'like'
  | 'locationPin'
  | 'mail'
  | 'navigationLeft'
  | 'plus'
  | 'printerReturnItems'
  | 'questionCircle'
  | 'receipt'
  | 'scanBarcode'
  | 'scrubsThread'
  | 'search'
  | 'shipping1'
  | 'shipping2'
  | 'stars'
  | 'tag1'
  | 'tag2'
  | 'toggleDown'
  | 'trash'
  | 'viewOff'
  | 'viewOn';

export function isSavageIconProps(icon: any): icon is SavageIconProps {
  return icon && typeof icon === 'object' && 'type' in icon;
}
type SavageIconProps = {
  type: SavageIconTypes;
  // Additional props.
  [key: string]: any;
};

type IconSet = {
  [key in IconName]: React.ElementType;
};

type SavageIconSet = {
  [key in IconName]: React.ElementType | SavageIconProps;
};

// FableticsIcons
export const fableticsIcons: IconSet = {
  addCircle: AddCircle,
  addToCart: AddToCart,
  alert: Alert,
  alertCircle: AlertCircle,
  alertTriangle: AlertTriangle,
  calendar: Calendar,
  camera: Camera,
  chevronLeft: CaretLeft,
  chevronRight: CaretRight,
  chevronDown: FaChevronDown,
  chevronUp: CaretUp,
  cart: CartIcon,
  checkCircle: CheckCircle,
  clock: Clock,
  close: Close,
  closeCircle: CloseCircle,
  deliveryStatus: DeliveryStatus,
  dislike: Dislike,
  edit: Edit,
  email: EmailIcon,
  emptyStar: EmptyStar,
  favorites: Favorites,
  filledStar: FilledStar,
  filter: Filter,
  gift: Gift,
  happyReturnsClock: HappyReturnsClock,
  happyReturnsLeaf: HappyReturnsLeaf,
  happyReturnsLocation: HappyReturnsLocation,
  happyReturnsOffer: HappyReturnsOffer,
  heart: Heart,
  home: Home,
  information: Information,
  leaf: Leaf,
  like: Like,
  locationPin: LocationPin,
  mail: Mail,
  navigationLeft: NavigationLeft,
  plus: Plus,
  printerReturnItems: PrinterReturnItems,
  questionCircle: QuestionCircle,
  receipt: Receipt,
  scanBarcode: ScanBarcode,
  scrubsThread: ScrubsThread,
  search: FaSearch,
  shipping1: Shipping1,
  shipping2: Shipping2,
  stars: Stars,
  tag1: Tag1,
  tag2: Tag2,
  toggleDown: ToggleDown,
  trash: FaTrash,
  viewOff: ViewOff,
  viewOn: ViewOn,
};

// YittyIcons
export const yittyIcons: IconSet = {
  addCircle: FaPlusCircle,
  addToCart: AddToCart,
  alert: FaRegBell,
  alertCircle: FaExclamationCircle,
  alertTriangle: FaExclamationTriangle,
  calendar: CalendarIcon,
  camera: FaCamera,
  chevronLeft: FaChevronLeft,
  chevronRight: FaChevronRight,
  chevronDown: FaChevronDown,
  chevronUp: FaChevronUp,
  cart: CartIcon,
  checkCircle: FaCheckCircle,
  clock: FaRegClock,
  close: FaTimes,
  closeCircle: FaTimesCircle,
  deliveryStatus: DeliveryStatus,
  dislike: FaRegThumbsDown,
  edit: FaPen,
  email: EmailIcon,
  emptyStar: FaRegStar,
  favorites: FavoriteIcon,
  filledStar: FaStar,
  filter: FaFilter,
  gift: FaGift,
  happyReturnsClock: HappyReturnsClock,
  happyReturnsLeaf: HappyReturnsLeaf,
  happyReturnsLocation: HappyReturnsLocation,
  happyReturnsOffer: HappyReturnsOffer,
  heart: FaHeart,
  home: Home,
  information: FaInfoCircle,
  leaf: Leaf,
  like: FaRegThumbsUp,
  locationPin: FaMapMarkerAlt,
  mail: Mail,
  navigationLeft: NavigationLeft,
  plus: FaPlus,
  printerReturnItems: PrinterReturnItems,
  questionCircle: FaRegQuestionCircle,
  receipt: Receipt,
  scanBarcode: ScanBarcode,
  scrubsThread: ScrubsThread,
  search: FaSearch,
  shipping1: Shipping1,
  shipping2: Shipping2,
  stars: Stars,
  tag1: Tag1,
  tag2: FaTag,
  toggleDown: ToggleDown,
  trash: FaTrash,
  viewOff: IoMdEyeOff,
  viewOn: IoMdEye,
};

// SavageIcons
export const savageIcons: SavageIconSet = {
  // Uses `SavageIcons`
  chevronLeft: {
    type: 'caretLeft',
  },
  chevronRight: {
    type: 'caretRight',
  },
  chevronUp: {
    type: 'caretUp',
  },
  chevronDown: {
    type: 'caretDown',
  },
  cart: {
    type: 'cart',
  },
  close: {
    type: 'close',
  },
  heart: {
    type: 'heart',
  },
  email: {
    type: 'email',
  },
  emptyStar: {
    type: 'starOutline',
    fill: 'none',
  },
  filledStar: {
    type: 'star',
  },
  search: {
    type: 'search',
  },
  trash: {
    type: 'trashcan',
  },
  // Fabletics Defaults
  addCircle: AddCircle,
  addToCart: AddToCart,
  alert: Alert,
  alertCircle: AlertCircle,
  alertTriangle: AlertTriangle,
  calendar: Calendar,
  camera: Camera,
  checkCircle: CheckCircle,
  clock: Clock,
  closeCircle: CloseCircle,
  deliveryStatus: DeliveryStatus,
  dislike: Dislike,
  edit: Edit,
  favorites: Favorites,
  filter: Filter,
  gift: Gift,
  happyReturnsClock: HappyReturnsClock,
  happyReturnsLeaf: HappyReturnsLeaf,
  happyReturnsLocation: HappyReturnsLocation,
  happyReturnsOffer: HappyReturnsOffer,
  home: Home,
  information: Information,
  leaf: Leaf,
  like: Like,
  locationPin: LocationPin,
  mail: Mail,
  navigationLeft: NavigationLeft,
  plus: Plus,
  printerReturnItems: PrinterReturnItems,
  questionCircle: QuestionCircle,
  receipt: Receipt,
  scanBarcode: ScanBarcode,
  scrubsThread: ScrubsThread,
  shipping1: Shipping1,
  shipping2: Shipping2,
  stars: Stars,
  tag1: Tag1,
  tag2: Tag2,
  toggleDown: ToggleDown,
  viewOff: ViewOff,
  viewOn: ViewOn,
};
